import type { PropKind } from '@/components'
import { Rng } from '@/core/Rng'
import type { ArenaDef, LightingProfile, PropSpec, SurfaceProfile } from './Arena'
import { chamferedRect, Circle, CompositeShape, Rect, ScaledShape } from './shapes'

const HALF_PI = Math.PI / 2

/**
 * Baseline light rig. Every arena starts here and overrides what makes it
 * itself. The values are deliberately much brighter than a "moody" first
 * instinct would suggest: on a fixed overhead camera the player has to read
 * obstacle silhouettes and the platform edge instantly, and atmosphere that
 * costs readability is atmosphere that costs rounds.
 */
const BASE_LIGHT: LightingProfile = {
  sunColor: 0xbcd2e8,
  sunIntensity: 4.1,
  // Raking from behind the arena rather than from the camera's side: shadows
  // then fall toward the viewer and give the flat platform some modelling.
  // Lighting from the front made every surface face the light and read flat.
  sunPosition: [-20, 26, -18],
  skyColor: 0x51707f,
  bounceColor: 0x1d2016,
  // Lifted after the floor material lost its specular response (which had been
  // faking a lot of the brightness before it was removed to kill a glare bug).
  hemiIntensity: 1.6,
  ambientColor: 0x2c3640,
  ambientIntensity: 1.05,
  // Fog and backdrop stay dark so the lit platform separates from the void.
  // Brightening them along with the lights was what turned the first pass into
  // a uniform grey wash with no sense of a drop.
  fogColor: 0x0a1014,
  fogDensity: 0.016,
  background: 0x05080b,
  domeColor: 0x0d151c,
  exposure: 1.3,
  edgeColor: 0xc98a4e,
}

function light(overrides: Partial<LightingProfile>): LightingProfile {
  return { ...BASE_LIGHT, ...overrides }
}

function surface(overrides: Partial<SurfaceProfile>): SurfaceProfile {
  return { tint: 0x6a6a5e, grime: 0.42, rust: 0.2, repeat: 3, seed: 7, ...overrides }
}

/** Evenly spaced props on a ring — barriers, pillars, lamp posts. */
function ring(
  kind: PropKind,
  count: number,
  radius: number,
  offset = 0,
  cx = 0,
  cz = 0,
): PropSpec[] {
  const out: PropSpec[] = []
  for (let i = 0; i < count; i++) {
    const a = offset + (i / count) * Math.PI * 2
    out.push({
      kind,
      x: cx + Math.sin(a) * radius,
      z: cz + Math.cos(a) * radius,
      angle: a,
      seed: i * 7 + 3,
    })
  }
  return out
}

/** Evenly spaced props along a segment — guard rails on straight edges. */
function line(
  kind: PropKind,
  count: number,
  x0: number,
  z0: number,
  x1: number,
  z1: number,
  angle = 0,
): PropSpec[] {
  const out: PropSpec[] = []
  for (let i = 0; i < count; i++) {
    const t = count === 1 ? 0.5 : i / (count - 1)
    out.push({
      kind,
      x: x0 + (x1 - x0) * t,
      z: z0 + (z1 - z0) * t,
      angle,
      seed: i * 13 + 5,
    })
  }
  return out
}

/** Deterministic clutter so every arena looks lived-in without hand placement. */
function scatter(
  kind: PropKind,
  count: number,
  seed: number,
  bounds: { x: number; z: number; rx: number; rz: number },
  keepOut: Array<{ x: number; z: number; r: number }> = [],
): PropSpec[] {
  const rng = new Rng(seed)
  const out: PropSpec[] = []
  let guard = 0
  while (out.length < count && guard++ < count * 60) {
    const x = bounds.x + rng.spread(bounds.rx)
    const z = bounds.z + rng.spread(bounds.rz)
    if (keepOut.some((k) => Math.hypot(x - k.x, z - k.z) < k.r)) continue
    if (out.some((p) => Math.hypot(x - p.x, z - p.z) < 1.6)) continue
    out.push({ kind, x, z, angle: rng.angle(), seed: rng.int(0, 9999) })
  }
  return out
}

// ──────────────────────────────────────────────────────────────── ПЛИТА ────

const slab: ArenaDef = {
  id: 'slab',
  name: 'ПЛИТА',
  blurb: 'Круглая бетонная площадка под холодной луной. Чистая дуэль без сюрпризов.',
  shape: new Circle(0, 0, 18),
  centerX: 0,
  centerZ: 0,
  extentX: 18,
  extentZ: 18,
  spawns: [
    { x: -11, z: 0, angle: HALF_PI },
    { x: 11, z: 0, angle: -HALF_PI },
  ],
  props: [
    { kind: 'block', x: 0, z: 0, angle: 0.35, scale: 1.15 },
    { kind: 'tree', x: -4.5, z: -6.5, seed: 11 },
    { kind: 'tree', x: 5.2, z: 6.1, seed: 27 },
    { kind: 'tree', x: -6.8, z: 5.4, scale: 0.85, seed: 41 },
    { kind: 'car', x: 0.5, z: -8.2, angle: 0.32, seed: 5 },
    { kind: 'car', x: -1.2, z: 8.6, angle: Math.PI - 0.5, seed: 9 },
    { kind: 'pillar', x: 7.5, z: -5.5, seed: 3 },
    { kind: 'pillar', x: -8.2, z: -2.5, seed: 8 },
    ...ring('barrier', 22, 17.2),
    ...scatter('crate', 9, 1001, { x: 0, z: 0, rx: 13, rz: 13 }, [
      { x: 0, z: 0, r: 3.2 },
      { x: 0.5, z: -8.2, r: 3 },
      { x: -1.2, z: 8.6, r: 3 },
      { x: -8.5, z: 0, r: 2.5 },
      { x: 8.5, z: 0, r: 2.5 },
    ]),
    { kind: 'barrel', x: 3.6, z: -3.4, seed: 2 },
    { kind: 'barrel', x: -3.9, z: 3.1, seed: 4 },
  ],
  // Even the learning arena gets one wandering hazard, so nobody grows up
  // thinking the middle of the platform is a safe place to stand.
  hazards: [{ kind: 'tornado', x: 4, z: 4, radius: 4, pull: 7, power: 9 }],
  // Moonlit and neutral: this is the arena people learn the game on, so it is
  // the most evenly lit of the four.
  lighting: light({}),
  surface: surface({ tint: 0x6d6d61, grime: 0.4, rust: 0.16, seed: 7 }),
  cameraZoom: 1,
}

// ─────────────────────────────────────────────────────────────── ЖЕЛЕЗО ────

const iron: ArenaDef = {
  id: 'iron',
  name: 'ЖЕЛЕЗО',
  blurb: 'Кладбище машин под натриевыми лампами. Коридоры, бочки, близкие края.',
  // Grown from 30 × 18 m: it was the smallest arena and it showed — two
  // fighters filled it and there was nowhere for a knockback to travel.
  shape: chamferedRect(0, 0, 24, 15, 5.5),
  centerX: 0,
  centerZ: 0,
  extentX: 24,
  extentZ: 15,
  spawns: [
    { x: -19.5, z: 0, angle: HALF_PI },
    { x: 19.5, z: 0, angle: -HALF_PI },
  ],
  props: [
    { kind: 'car', x: -7.5, z: -5.2, angle: 0.06, seed: 12 },
    { kind: 'car', x: -1.5, z: -5.6, angle: -0.1, seed: 31 },
    { kind: 'car', x: 5.5, z: -5.1, angle: 0.14, seed: 44 },
    { kind: 'car', x: -5.5, z: 5.4, angle: Math.PI + 0.1, seed: 17 },
    { kind: 'car', x: 1.8, z: 5.7, angle: Math.PI - 0.05, seed: 22 },
    { kind: 'car', x: 8.6, z: 5.2, angle: Math.PI + 0.2, seed: 51 },
    { kind: 'block', x: 0, z: 0, angle: 0.2, scale: 0.9 },
    { kind: 'pillar', x: -12, z: -6.4, seed: 1 },
    { kind: 'pillar', x: 12, z: 6.4, seed: 6 },
    { kind: 'pillar', x: -12, z: 6.4, seed: 13 },
    { kind: 'pillar', x: 12, z: -6.4, seed: 19 },
    { kind: 'barrel', x: -4.2, z: -1.8, seed: 7 },
    { kind: 'barrel', x: 4.4, z: 1.9, seed: 15 },
    { kind: 'barrel', x: -9.3, z: 2.2, seed: 23 },
    { kind: 'barrel', x: 9.1, z: -2.3, seed: 29 },
    ...scatter('crate', 9, 2002, { x: 0, z: 0, rx: 12, rz: 6.5 }, [
      { x: 0, z: 0, r: 2.6 },
      { x: -11.5, z: 0, r: 2.5 },
      { x: 11.5, z: 0, r: 2.5 },
      { x: -4.2, z: -1.8, r: 1.4 },
      { x: 4.4, z: 1.9, r: 1.4 },
    ]),
    { kind: 'car', x: -13.5, z: 0, angle: HALF_PI + 0.1, seed: 61 },
    { kind: 'car', x: 13.5, z: 0.4, angle: HALF_PI - 0.12, seed: 66 },
    { kind: 'crate', x: -16.5, z: -4.2, seed: 71 },
    { kind: 'crate', x: 16.5, z: 4.2, seed: 74 },
    // Four posts, not seven: at 2.5 m spacing a 1.24 m fighter had one
    // centimetre of clearance and the short rims were effectively sealed.
    ...line('barrier', 5, -23.2, -9.5, -23.2, 9.5, HALF_PI),
    ...line('barrier', 5, 23.2, -9.5, 23.2, 9.5, HALF_PI),
    ...line('barrier', 13, -20, -14.4, 20, -14.4, 0),
    ...line('barrier', 13, -20, 14.4, 20, 14.4, 0),
  ],
  hazards: [
    { kind: 'electro', x: -8, z: 0, radius: 4.6, power: 11, interval: 4.4, phase: 0 },
    { kind: 'electro', x: 8, z: 0, radius: 4.6, power: 11, interval: 4.4, phase: 2.2 },
  ],
  // Sodium vapour: warm amber key, dirty orange haze, deep blue shadows. The
  // colour contrast between light and shadow is what makes the car wrecks read.
  lighting: light({
    sunColor: 0xffb066,
    sunIntensity: 4.3,
    sunPosition: [18, 30, -14],
    skyColor: 0x3b4a6a,
    bounceColor: 0x2a1c10,
    hemiIntensity: 1.2,
    ambientColor: 0x38302c,
    ambientIntensity: 0.9,
    fogColor: 0x140f0a,
    fogDensity: 0.018,
    background: 0x080605,
    domeColor: 0x140e09,
    exposure: 1.26,
    edgeColor: 0xc79b57,
  }),
  surface: surface({ tint: 0x6b6152, grime: 0.5, rust: 0.4, repeat: 4, seed: 23 }),
  cameraZoom: 1,
}

// ──────────────────────────────────────────────────────────────── КРЕСТ ────

const cross: ArenaDef = {
  id: 'cross',
  name: 'КРЕСТ',
  blurb: 'Крест из узких рукавов в ядовитом свечении. Огромный периметр и «жарки» на концах.',
  // Arms are 7.2 m wide rather than 6: at 6 a single push from the middle of an
  // arm slid a fighter clean off, and the harness settled rounds in under three
  // seconds. Radar should be the most dangerous arena, not a coin toss.
  shape: new CompositeShape([
    new Circle(0, 0, 11),
    new Rect(0, -13.5, 5.2, 9.5),
    new Rect(0, 13.5, 5.2, 9.5),
    new Rect(-13.5, 0, 9.5, 5.2),
    new Rect(13.5, 0, 9.5, 5.2),
  ]),
  centerX: 0,
  centerZ: 0,
  extentX: 23,
  extentZ: 23,
  spawns: [
    { x: -17, z: 0, angle: HALF_PI },
    { x: 17, z: 0, angle: -HALF_PI },
  ],
  props: [
    { kind: 'block', x: 0, z: 0, angle: 0, scale: 1.5 },
    { kind: 'pillar', x: -4.6, z: -4.6, seed: 2 },
    { kind: 'pillar', x: 4.6, z: 4.6, seed: 5 },
    { kind: 'pillar', x: -4.6, z: 4.6, seed: 9 },
    { kind: 'pillar', x: 4.6, z: -4.6, seed: 14 },
    { kind: 'crate', x: 0, z: -8.5, seed: 21 },
    { kind: 'crate', x: 0.9, z: -11, seed: 24 },
    { kind: 'crate', x: 0, z: 8.5, seed: 33 },
    { kind: 'crate', x: -0.8, z: 11.2, seed: 36 },
    { kind: 'crate', x: -8.5, z: 0.4, seed: 42 },
    { kind: 'crate', x: 8.7, z: -0.5, seed: 47 },
    { kind: 'barrel', x: -11, z: 1.4, seed: 8 },
    { kind: 'barrel', x: 11, z: -1.4, seed: 16 },
    { kind: 'tree', x: -3.2, z: 5.9, scale: 0.8, seed: 55 },
    { kind: 'tree', x: 3.4, z: -5.8, scale: 0.9, seed: 61 },
  ],
  // Burners sit at the very tips, clear of the spawns at ±14, and the two on
  // the fighters' axis share a phase so neither player is singled out.
  hazards: [
    { kind: 'burner', x: 0, z: -21.5, radius: 3, phase: 0.9 },
    { kind: 'burner', x: 0, z: 21.5, radius: 3, phase: 2.6 },
    { kind: 'burner', x: -21.5, z: 0, radius: 3, phase: 0 },
    { kind: 'burner', x: 21.5, z: 0, radius: 3, phase: 0 },
    { kind: 'electro', x: 0, z: 0, radius: 4.4, power: 10, interval: 5 },
  ],
  // Anomalous green from below, hard white key from above: the narrow arms need
  // maximum edge contrast, and the acid bounce light does exactly that.
  lighting: light({
    sunColor: 0xe8f4d8,
    sunIntensity: 4.5,
    sunPosition: [-14, 30, -20],
    skyColor: 0x4a6a4c,
    bounceColor: 0x24361a,
    hemiIntensity: 1.5,
    ambientColor: 0x2e3a2c,
    ambientIntensity: 0.9,
    fogColor: 0x0a1009,
    fogDensity: 0.014,
    background: 0x040705,
    domeColor: 0x0c140d,
    exposure: 1.32,
    edgeColor: 0x5f9c2c,
  }),
  surface: surface({ tint: 0x66705f, grime: 0.36, rust: 0.12, repeat: 3, seed: 41 }),
  cameraZoom: 1,
}

// ─────────────────────────────────────────────────────────────── КОЛЬЦО ────

const ringArena: ArenaDef = {
  id: 'ring',
  name: 'КОЛЬЦО',
  blurb: 'Кольцо над провалом в ледяном свете. Упасть можно наружу — и внутрь.',
  shape: new CompositeShape([new Circle(0, 0, 19.5)], [new Circle(0, 0, 5.5)]),
  centerX: 0,
  centerZ: 0,
  extentX: 19.5,
  extentZ: 19.5,
  // Deliberately not antipodal: on a ring, opposite spawns put the central
  // hole directly on the line between the fighters, and a straight approach
  // walks both of them into the void before anyone throws a punch.
  spawns: [
    { x: -6.6, z: -7.9, angle: HALF_PI },
    { x: 6.6, z: -7.9, angle: -HALF_PI },
  ],
  props: [
    ...ring('pillar', 8, 9.6, 0.3),
    { kind: 'car', x: 0, z: -11.5, angle: 0.1, seed: 3 },
    { kind: 'car', x: 0, z: 11.5, angle: Math.PI - 0.15, seed: 18 },
    { kind: 'tree', x: -11.8, z: -5.2, seed: 26 },
    { kind: 'tree', x: 11.4, z: 5.6, seed: 39 },
    { kind: 'barrel', x: -6.6, z: -6.6, seed: 11 },
    { kind: 'barrel', x: 6.6, z: 6.6, seed: 20 },
    { kind: 'barrel', x: -6.9, z: 6.3, seed: 30 },
    { kind: 'barrel', x: 6.9, z: -6.3, seed: 40 },
    // Thinned from 26: a dense rail made being launched outward survivable,
    // which left the inner hole as the only real way to lose the round.
    ...ring('barrier', 16, 18.8),
    ...scatter('crate', 9, 3003, { x: 0, z: 0, rx: 13.5, rz: 13.5 }, [
      { x: 0, z: 0, r: 7.5 },
      { x: -10.2, z: 0, r: 2.5 },
      { x: 10.2, z: 0, r: 2.5 },
    ]),
  ],
  // The central pull is deliberately weak and short-ranged now: at radius 9.5
  // it drained every crate and barrel into the hole within fifteen seconds and
  // made every ring-out an inward one, so the outer rim never mattered.
  hazards: [
    { kind: 'vortex', x: 0, z: 0, radius: 6.5, pull: 2.5 },
    { kind: 'tornado', x: -11, z: 6, radius: 4, pull: 7.5, power: 9 },
  ],
  // Glacial cyan, almost no haze. The clearest air of the four, because on a
  // ring both edges matter and neither may be lost to fog.
  lighting: light({
    sunColor: 0xcfe6ff,
    sunIntensity: 4.2,
    sunPosition: [22, 28, -16],
    skyColor: 0x50748f,
    bounceColor: 0x18242c,
    hemiIntensity: 1.45,
    ambientColor: 0x2a3a46,
    ambientIntensity: 0.95,
    fogColor: 0x080e14,
    fogDensity: 0.012,
    background: 0x04070a,
    domeColor: 0x0b1219,
    exposure: 1.32,
    edgeColor: 0x4e93ad,
  }),
  surface: surface({ tint: 0x707a72, grime: 0.34, rust: 0.1, repeat: 3, seed: 59 }),
  cameraZoom: 1,
}

// ──────────────────────────────────────────────────────────────── МОСТ ────

const bridge: ArenaDef = {
  id: 'bridge',
  name: 'МОСТ',
  blurb: 'Два острова и узкая перемычка. Идти на врага придётся по ниточке.',
  shape: new CompositeShape([
    new Circle(-13.5, 0, 10),
    new Circle(13.5, 0, 10),
    new Rect(0, 0, 6, 3),
  ]),
  centerX: 0,
  centerZ: 0,
  extentX: 23.5,
  extentZ: 10,
  spawns: [
    { x: -17, z: 0, angle: HALF_PI },
    { x: 17, z: 0, angle: -HALF_PI },
  ],
  props: [
    { kind: 'block', x: -13.5, z: -3.5, angle: 0.4, scale: 0.85 },
    { kind: 'block', x: 13.5, z: 3.5, angle: -0.3, scale: 0.85 },
    { kind: 'car', x: -10, z: 5.5, angle: 0.2, seed: 4 },
    { kind: 'car', x: 10, z: -5.5, angle: Math.PI + 0.2, seed: 12 },
    { kind: 'tree', x: -15.5, z: 4, seed: 21 },
    { kind: 'tree', x: 15.5, z: -4, seed: 33 },
    { kind: 'pillar', x: -6.5, z: 2.2, seed: 6 },
    { kind: 'pillar', x: 6.5, z: -2.2, seed: 9 },
    { kind: 'barrel', x: -8.5, z: -1.5, seed: 2 },
    { kind: 'barrel', x: 8.5, z: 1.5, seed: 5 },
    // Rails only on the outer halves of the islands: a full ring put a post
    // squarely in each bridge mouth and narrowed the crossing to two slots
    // barely wider than a fighter.
    ...ring('barrier', 16, 9.5, 0.2, -13.5, 0).filter((p) => p.x < -14),
    ...ring('barrier', 16, 9.5, 0.2, 13.5, 0).filter((p) => p.x > 14),
    ...scatter('crate', 6, 5005, { x: 0, z: 0, rx: 15, rz: 5 }, [
      { x: 0, z: 0, r: 6 },
      { x: -14, z: 0, r: 2.5 },
      { x: 14, z: 0, r: 2.5 },
    ]),
  ],
  // The bridge itself is the interesting square metre on this map, so that is
  // exactly where the danger lives.
  // A narrower discharge so the bridge is a risk to time rather than a dice
  // roll, and a tornado on each island so neither spawn starts inside one.
  hazards: [
    { kind: 'electro', x: 0, z: 0, radius: 3, power: 12, interval: 3.8 },
    { kind: 'tornado', x: -9, z: 4, radius: 3.4, pull: 7, power: 9 },
    { kind: 'tornado', x: 9, z: -4, radius: 3.4, pull: 7, power: 9 },
  ],
  lighting: light({
    sunColor: 0xd8c8ff,
    sunIntensity: 4.2,
    // Never place the key light on the camera's axis: directly behind the
    // arena it puts its specular highlight in the middle of the platform, and
    // a broad sheen on wet-looking concrete reads as a hole full of light.
    sunPosition: [-17, 28, -19],
    skyColor: 0x4a4470,
    bounceColor: 0x201a2c,
    hemiIntensity: 1.4,
    ambientColor: 0x322c44,
    ambientIntensity: 0.9,
    fogColor: 0x0c0a12,
    fogDensity: 0.013,
    background: 0x06050a,
    domeColor: 0x100d18,
    exposure: 1.3,
    edgeColor: 0x9a7fc9,
  }),
  surface: surface({ tint: 0x6a6672, grime: 0.44, rust: 0.18, repeat: 3, seed: 83 }),
  cameraZoom: 1,
}

// ─────────────────────────────────────────────────────────── ШЕСТЕРНЯ ────

/** A disc with six bites taken out of it — every notch is a place to die. */
const gearShape = new CompositeShape(
  [new Circle(0, 0, 19)],
  Array.from({ length: 6 }, (_, i) => {
    const a = (i / 6) * Math.PI * 2 + 0.25
    return new Circle(Math.sin(a) * 19.5, Math.cos(a) * 19.5, 6.2)
  }),
)

const gear: ArenaDef = {
  id: 'gear',
  name: 'ШЕСТЕРНЯ',
  blurb: 'Диск с шестью вырезами. Край здесь то ближе, то дальше — и всегда рядом.',
  shape: gearShape,
  centerX: 0,
  centerZ: 0,
  extentX: 19,
  extentZ: 19,
  spawns: [
    { x: -12.5, z: 0, angle: HALF_PI },
    { x: 12.5, z: 0, angle: -HALF_PI },
  ],
  props: [
    { kind: 'block', x: 0, z: 0, angle: 0.5, scale: 1.3 },
    ...ring('pillar', 6, 11.5, 0.25),
    { kind: 'car', x: -6.5, z: -8.5, angle: 0.5, seed: 8 },
    { kind: 'car', x: 6.5, z: 8.5, angle: Math.PI + 0.5, seed: 16 },
    { kind: 'tree', x: 9, z: -6, seed: 44 },
    { kind: 'tree', x: -9, z: 6, seed: 52 },
    { kind: 'barrel', x: -4.5, z: 5.5, seed: 3 },
    { kind: 'barrel', x: 4.5, z: -5.5, seed: 7 },
    { kind: 'barrel', x: 0, z: 8.5, seed: 11 },
    ...scatter('crate', 8, 6006, { x: 0, z: 0, rx: 11, rz: 11 }, [
      { x: 0, z: 0, r: 4 },
      { x: -10, z: 0, r: 2.5 },
      { x: 10, z: 0, r: 2.5 },
    ]),
  ],
  hazards: [
    { kind: 'tornado', x: 6, z: -6, radius: 4.2, pull: 8, power: 10 },
    { kind: 'tornado', x: -6, z: 6, radius: 4.2, pull: 8, power: 10 },
    { kind: 'burner', x: 0, z: -11, radius: 3.4, phase: 1.2 },
    { kind: 'burner', x: 0, z: 11, radius: 3.4, phase: 2.9 },
  ],
  lighting: light({
    sunColor: 0xffd9a8,
    sunIntensity: 4.0,
    sunPosition: [16, 24, -20],
    skyColor: 0x6a4a3a,
    bounceColor: 0x2c1e14,
    hemiIntensity: 1.3,
    ambientColor: 0x3a2e28,
    ambientIntensity: 0.95,
    fogColor: 0x120d09,
    fogDensity: 0.014,
    background: 0x070504,
    domeColor: 0x120d0a,
    exposure: 1.28,
    edgeColor: 0xd08a4a,
  }),
  surface: surface({ tint: 0x74695a, grime: 0.46, rust: 0.3, repeat: 3, seed: 97 }),
  cameraZoom: 1,
}

/**
 * Arenas are authored at their final size.
 *
 * There used to be a global 0.8 shrink applied here, added to make fighters
 * read on screen. It made the platforms so tight that a single push ended the
 * round — the fight had no room to happen in. Readability is handled by the
 * character scale and the camera fit instead, and `ScaledShape` remains in
 * `shapes.ts` should one arena ever need resizing without moving fifty
 * hand-placed props.
 */
export const ARENAS: ArenaDef[] = [slab, iron, cross, ringArena, bridge, gear]

export function arenaById(id: string): ArenaDef {
  return ARENAS.find((a) => a.id === id) ?? ARENAS[0]
}

/** Sizes offered in the menu, as multipliers of the authored footprint. */
export const ARENA_SIZES: Array<{ scale: number; label: string }> = [
  { scale: 0.85, label: 'ТЕСНАЯ' },
  { scale: 1, label: 'ОБЫЧНАЯ' },
  { scale: 1.3, label: 'ПРОСТОРНАЯ' },
  { scale: 1.7, label: 'ОГРОМНАЯ' },
]

/**
 * Returns the arena resized about its centre.
 *
 * Everything positional scales — the footprint, the spawn ring, the scenery,
 * the anomalies — while the *things* keep their size: crates, cars and fighters
 * stay as they are, so a bigger arena means more open ground rather than the
 * same picture zoomed out. That is the difference between a map that feels
 * larger and a map that only measures larger.
 *
 * Returns the original object at scale 1 so the common case allocates nothing.
 */
export function scaledArena(def: ArenaDef, scale: number): ArenaDef {
  if (Math.abs(scale - 1) < 0.001) return def
  return {
    ...def,
    sizeScale: scale,
    shape: new ScaledShape(def.shape, scale),
    extentX: def.extentX * scale,
    extentZ: def.extentZ * scale,
    spawns: def.spawns.map((s) => ({ ...s, x: s.x * scale, z: s.z * scale })) as ArenaDef['spawns'],
    props: def.props.map((p) => ({ ...p, x: p.x * scale, z: p.z * scale })),
    // Hazard radii are gameplay, not geometry: a tornado that grew with the
    // platform would cover exactly as much of it, and the extra ground would
    // buy the players nothing.
    hazards: def.hazards.map((h) => ({ ...h, x: h.x * scale, z: h.z * scale })),
  }
}
